import React, { useMemo, useState } from 'react';
import { buildSheetRow, buildSheetTotals, faixaAtingida } from '../../utils/funnelSheet';
import ClinicFunnelUnitCard from './ClinicFunnelUnitCard';

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtBRL0 = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(Number(v) || 0);
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtPct = (v) => `${(Number(v) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const DASH = <span className="text-slate-300">—</span>;

const MODES = [
  { id: 'plan', label: 'Planejado' },
  { id: 'real', label: 'Realizado' },
  { id: 'both', label: 'Lado a lado' }
];

// plan: so planejado | real: so realizado | both: "realizado / planejado"
const pair = (mode, plan, real, fmt = fmtNum) => {
  if (mode === 'plan') return fmt(plan);
  if (mode === 'real') return fmt(real);
  return (
    <>
      <span className="font-semibold text-slate-900">{fmt(real)}</span>
      <span className="text-slate-400"> / {fmt(plan)}</span>
    </>
  );
};

const FAIXA_CLASS = 'bg-emerald-100 text-emerald-800 font-bold rounded px-1';
const faixaCell = (value, active) => (active ? <span className={FAIXA_CLASS}>{fmtNum(value)}</span> : fmtNum(value));

const GROUPS = {
  meta: { label: 'Meta', head: 'bg-slate-100 text-slate-700', sub: 'bg-slate-50' },
  crc: { label: 'Agência CRC', head: 'bg-sky-100 text-sky-800', sub: 'bg-sky-50' },
  rec: { label: 'Recepção 25%', head: 'bg-violet-100 text-violet-800', sub: 'bg-violet-50' },
  com: { label: 'Comercial', head: 'bg-amber-100 text-amber-800', sub: 'bg-amber-50' }
};

const ALL = ['plan', 'real', 'both'];
const NO_REAL = ['plan', 'both'];

function buildColumns(sharePct) {
  const crcFaixa = (tier, label, bonus) => ({
    key: `crc-${tier}`, label, sub: bonus, modes: ALL,
    cell: (r, mode) => faixaCell(r.crcFaixas[tier], mode !== 'plan' && faixaAtingida(r.real.comparecimentos, r.crcFaixas) === tier),
    total: (t, mode) => faixaCell(t.crcFaixas[tier], false)
  });
  const recFaixa = (tier, label, bonus) => ({
    key: `rec-${tier}`, label, sub: bonus, modes: NO_REAL,
    cell: (r) => (r.recepcao ? fmtNum(r.recepcao.faixas[tier]) : DASH),
    total: (t) => (t.recepcao ? fmtNum(t.recepcao.faixas[tier]) : DASH)
  });
  const dezCrc = (i) => ({
    key: `crc-dz${i}`, label: `${i + 1}ª dezena`, modes: ALL,
    cell: (r, mode) => pair(mode, r.dezenasCrc[i], r.real.dezenasComp[i]),
    total: (t, mode) => pair(mode, t.dezenasCrc[i], t.real.dezenasComp[i])
  });
  const dezCom = (i) => ({
    key: `com-dz${i}`, label: `${i + 1}ª dezena`, sub: 'R$', modes: NO_REAL,
    cell: (r) => (r.comercial ? fmtBRL0(r.comercial.dezenasValor[i]) : DASH),
    total: (t) => (t.comercial ? fmtBRL0(t.comercial.dezenasValor[i]) : DASH)
  });

  return [
    { key: 'meta', group: 'meta', label: 'Meta total', modes: ALL, first: true,
      cell: (r, mode) => pair(mode, r.meta, r.real.receita, fmtBRL0),
      total: (t, mode) => pair(mode, t.meta, t.real.receita, fmtBRL0) },
    { key: 'metaAg', group: 'meta', label: `Meta agência ${sharePct}`, modes: NO_REAL,
      cell: (r) => fmtBRL0(r.metaCrc), total: (t) => fmtBRL0(t.metaCrc) },
    { key: 'inv', group: 'meta', label: 'Investimento', modes: ALL,
      cell: (r, mode) => pair(mode, r.investimento, r.real.investimento, fmtBRL0),
      total: (t, mode) => pair(mode, t.investimento, t.real.investimento, fmtBRL0) },
    { key: 'pctInv', group: 'meta', label: '% Invest.', modes: ALL,
      cell: (r, mode) => pair(mode, r.pctInvest, r.real.receita > 0 ? r.real.investimento / r.real.receita : 0, fmtPct),
      total: (t, mode) => pair(mode, t.pctInvest, t.real.receita > 0 ? t.real.investimento / t.real.receita : 0, fmtPct) },
    { key: 'tkm', group: 'meta', label: 'TKM unidade', modes: NO_REAL,
      cell: (r) => fmtBRL0(r.ticketUnidade), total: (t) => fmtBRL0(t.ticketUnidade) },

    { key: 'crc-fech', group: 'crc', label: 'Fech.', sub: '30%', modes: ALL, first: true,
      cell: (r, mode) => pair(mode, r.fechamentos, r.real.fechamentos),
      total: (t, mode) => pair(mode, t.fechamentos, t.real.fechamentos) },
    { key: 'crc-comp', group: 'crc', label: 'Compar.', sub: '60%', modes: ALL,
      cell: (r, mode) => pair(mode, r.comparecimentos, r.real.comparecimentos),
      total: (t, mode) => pair(mode, t.comparecimentos, t.real.comparecimentos) },
    { key: 'crc-agend', group: 'crc', label: 'Agend.', sub: '30%', modes: ALL,
      cell: (r, mode) => pair(mode, r.agendamentos, r.real.agendamentos),
      total: (t, mode) => pair(mode, t.agendamentos, t.real.agendamentos) },
    { key: 'crc-leads', group: 'crc', label: 'Leads', modes: ALL,
      cell: (r, mode) => pair(mode, r.leads, r.real.leads),
      total: (t, mode) => pair(mode, t.leads, t.real.leads) },
    { key: 'crc-cpl', group: 'crc', label: 'CPL médio', modes: ALL,
      cell: (r, mode) => pair(mode, r.cpl, r.real.leads > 0 ? r.real.investimento / r.real.leads : 0, fmtBRL),
      total: (t, mode) => pair(mode, t.cpl, t.real.leads > 0 ? t.real.investimento / t.real.leads : 0, fmtBRL) },
    { ...crcFaixa('low', '0–80%', 'R$ 7'), group: 'crc', first: false },
    { ...crcFaixa('mid', '80–99,99%', 'R$ 10'), group: 'crc' },
    { ...crcFaixa('high', '100%+', 'R$ 12'), group: 'crc' },
    { ...dezCrc(0), group: 'crc' },
    { ...dezCrc(1), group: 'crc' },
    { ...dezCrc(2), group: 'crc' },

    { key: 'rec-meta', group: 'rec', label: 'Meta', sub: 'R$', modes: NO_REAL, first: true,
      cell: (r) => (r.recepcao ? fmtBRL0(r.recepcao.meta) : DASH),
      total: (t) => (t.recepcao ? fmtBRL0(t.recepcao.meta) : DASH) },
    { key: 'rec-fech', group: 'rec', label: 'Fech.', sub: '30%', modes: NO_REAL,
      cell: (r) => (r.recepcao ? fmtNum(r.recepcao.fechamentos) : DASH),
      total: (t) => (t.recepcao ? fmtNum(t.recepcao.fechamentos) : DASH) },
    { key: 'rec-comp', group: 'rec', label: 'Compar.', sub: '60%', modes: NO_REAL,
      cell: (r) => (r.recepcao ? fmtNum(r.recepcao.comparecimentos) : DASH),
      total: (t) => (t.recepcao ? fmtNum(t.recepcao.comparecimentos) : DASH) },
    { key: 'rec-agend', group: 'rec', label: 'Agend.', sub: '30%', modes: NO_REAL,
      cell: (r) => (r.recepcao ? fmtNum(r.recepcao.agendamentos) : DASH),
      total: (t) => (t.recepcao ? fmtNum(t.recepcao.agendamentos) : DASH) },
    { key: 'rec-ind', group: 'rec', label: 'Indicações', modes: NO_REAL,
      cell: (r) => (r.recepcao ? fmtNum(r.recepcao.indicacoes) : DASH),
      total: (t) => (t.recepcao ? fmtNum(t.recepcao.indicacoes) : DASH) },
    { ...recFaixa('low', '0–80%', 'R$ 7'), group: 'rec' },
    { ...recFaixa('mid', '80–99,99%', 'R$ 10'), group: 'rec' },
    { ...recFaixa('high', '100%+', 'R$ 12'), group: 'rec' },

    { key: 'com-fech', group: 'com', label: 'Fechamentos', sub: '30%', modes: NO_REAL, first: true,
      cell: (r) => (r.comercial ? fmtNum(r.comercial.fechamentos) : DASH),
      total: (t) => (t.comercial ? fmtNum(t.comercial.fechamentos) : DASH) },
    { key: 'com-val', group: 'com', label: 'Valor por ticket', modes: NO_REAL,
      cell: (r) => (r.comercial ? fmtBRL0(r.comercial.valorTicket) : DASH),
      total: (t) => (t.comercial ? fmtBRL0(t.comercial.valorTicket) : DASH) },
    { ...dezCom(0), group: 'com' },
    { ...dezCom(1), group: 'com' },
    { ...dezCom(2), group: 'com' },
    { key: 'com-tkm', group: 'com', label: 'Ticket médio unid.', modes: NO_REAL,
      cell: (r) => (r.comercial ? fmtBRL0(r.comercial.ticketUnidade) : DASH),
      total: (t) => (t.comercial && t.comercial.ticketUnidade ? fmtBRL0(t.comercial.ticketUnidade) : DASH) }
  ];
}

// Visao sem metas (showFunnelAndGoals = false): apenas realizado, sem planejado/faixas/bonus
const REAL_ONLY_COLUMNS = [
  { key: 'inv', group: 'crc', label: 'Investimento', first: true,
    cell: (r) => fmtBRL0(r.real.investimento), total: (t) => fmtBRL0(t.real.investimento) },
  { key: 'cpl', group: 'crc', label: 'CPL real',
    cell: (r) => fmtBRL(r.real.leads > 0 ? r.real.investimento / r.real.leads : 0),
    total: (t) => fmtBRL(t.real.leads > 0 ? t.real.investimento / t.real.leads : 0) },
  { key: 'leads', group: 'crc', label: 'Leads', cell: (r) => fmtNum(r.real.leads), total: (t) => fmtNum(t.real.leads) },
  { key: 'agend', group: 'crc', label: 'Agend.', cell: (r) => fmtNum(r.real.agendamentos), total: (t) => fmtNum(t.real.agendamentos) },
  { key: 'comp', group: 'crc', label: 'Compar.', cell: (r) => fmtNum(r.real.comparecimentos), total: (t) => fmtNum(t.real.comparecimentos) },
  { key: 'fech', group: 'crc', label: 'Fech.', cell: (r) => fmtNum(r.real.fechamentos), total: (t) => fmtNum(t.real.fechamentos) }
];

const stickyName = 'sticky left-0 shadow-[3px_0_5px_-2px_rgba(15,23,42,0.18)]';

function Segmented({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-xs font-semibold">
      {options.map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`px-2.5 py-1 rounded transition-colors ${value === o.id ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function ClinicFunnelSheetTable({ report, showFunnelAndGoals = true, onSelectUnit }) {
  const [mode, setMode] = useState('plan');
  const [view, setView] = useState('table'); // 'table' | 'cards'

  const rows = useMemo(() => (report?.units || []).map(u => ({ unit: u, row: buildSheetRow(u, report) })), [report]);
  const totals = useMemo(() => buildSheetTotals(rows.map(r => r.row)), [rows]);

  const effectiveMode = showFunnelAndGoals ? mode : 'real';
  const sharePct = report?.agencyShare != null
    ? `${(Number(report.agencyShare) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    : '100%';

  const columns = useMemo(() => {
    if (!showFunnelAndGoals) return REAL_ONLY_COLUMNS;
    return buildColumns(sharePct).filter(c => c.modes.includes(effectiveMode));
  }, [showFunnelAndGoals, sharePct, effectiveMode]);

  const groups = useMemo(() => {
    const out = [];
    columns.forEach(c => {
      const last = out[out.length - 1];
      if (last && last.id === c.group) last.span += 1;
      else out.push({ id: c.group, span: 1 });
    });
    return out;
  }, [columns]);

  if (!rows.length) return null;

  const hasLegacy = rows.some(r => r.row.legacy);
  const th = 'px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-600 text-right whitespace-nowrap';
  const td = 'px-2 py-1.5 text-xs text-slate-700 text-right whitespace-nowrap tabular-nums';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-headline font-bold">Unidades</h3>
          <p className="text-xs text-slate-500">Clique em uma unidade para abrir o card e os gráficos.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showFunnelAndGoals && view === 'table' && <Segmented options={MODES} value={mode} onChange={setMode} />}
          {showFunnelAndGoals && (
            <Segmented
              options={[{ id: 'table', label: 'Tabela' }, { id: 'cards', label: 'Cards' }]}
              value={view}
              onChange={setView}
            />
          )}
        </div>
      </div>

      {showFunnelAndGoals && view === 'cards' ? (
        <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {rows.map(({ unit, row }) => (
            <ClinicFunnelUnitCard
              key={row.id}
              row={row}
              referenceMonth={report.referenceMonth}
              compact
              onClick={() => onSelectUnit(unit)}
            />
          ))}
        </div>
      ) : (
        <>
          <div className="overflow-auto max-h-[75vh]">
            <table className="border-separate border-spacing-0 min-w-full text-left font-body">
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    className={`${stickyName} top-0 z-30 bg-slate-100 px-3 py-2 text-[10px] font-bold uppercase text-slate-600 min-w-[200px] border-b border-slate-200 align-bottom`}
                  >
                    Unidade
                  </th>
                  {groups.map((g, i) => (
                    <th
                      key={`${g.id}-${i}`}
                      colSpan={g.span}
                      className={`sticky top-0 z-20 h-8 text-center text-[11px] font-bold uppercase tracking-wider border-l border-white ${GROUPS[g.id].head}`}
                    >
                      {GROUPS[g.id].label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {columns.map(c => (
                    <th
                      key={c.key}
                      className={`${th} sticky top-8 z-20 border-b border-slate-200 ${GROUPS[c.group].sub} ${c.first ? 'border-l border-l-slate-200' : ''}`}
                    >
                      <span className="block leading-tight">{c.label}</span>
                      {c.sub && <span className="block text-[9px] font-semibold normal-case text-slate-400">{c.sub}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ unit, row }) => (
                  <tr key={row.id} onClick={() => onSelectUnit(unit)} className="group cursor-pointer">
                    <td className={`${stickyName} z-10 bg-white group-hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-800 whitespace-nowrap border-b border-slate-100`}>
                      {row.name}
                    </td>
                    {columns.map(c => (
                      <td
                        key={c.key}
                        className={`${td} border-b border-slate-100 group-hover:bg-slate-50 ${c.first ? 'border-l border-l-slate-100' : ''}`}
                      >
                        {c.cell(row, effectiveMode)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className={`${stickyName} bottom-0 z-20 bg-slate-100 px-3 py-2 text-xs font-black text-slate-900 whitespace-nowrap border-t-2 border-slate-300`}>
                    Grupo (total)
                  </td>
                  {columns.map(c => (
                    <td
                      key={c.key}
                      className={`${td} sticky bottom-0 z-10 bg-slate-100 font-bold text-slate-900 border-t-2 border-slate-300 ${c.first ? 'border-l border-l-slate-200' : ''}`}
                    >
                      {c.total(totals, effectiveMode)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
          {showFunnelAndGoals && effectiveMode !== 'plan' && (
            <p className="px-4 sm:px-6 py-2 text-[11px] text-slate-500 border-t border-slate-100">
              {effectiveMode === 'both' ? 'Células mostram realizado / planejado. ' : ''}
              Faixa de bônus destacada = maior limite de comparecimentos já atingido pelo CRC.
              Recepção e Comercial não possuem dado realizado{effectiveMode === 'real' ? ' e ficam ocultos nesta visão' : ''}.
            </p>
          )}
          {hasLegacy && showFunnelAndGoals && (
            <p className="px-4 sm:px-6 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100">
              Funil legado (100% da meta na Agência): colunas de Recepção e Comercial não se aplicam.
            </p>
          )}
        </>
      )}
    </div>
  );
}
